import dayjs from 'dayjs';
import { ArrowLeft, ChevronRight, Fuel, MapPin, Route, Store } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getStation, getStationBenchmarks, getStationHistory } from '../api/endpoints';
import BenchmarkChart from '../components/charts/BenchmarkChart';
import PriceTrendChart from '../components/charts/PriceTrendChart';
import ErrorState from '../components/feedback/ErrorState';
import LoadingState from '../components/feedback/LoadingState';
import PriceChangePill from '../components/public/PriceChangePill';
import SectionLabel from '../components/public/SectionLabel';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Map, MapMarker, MapPopup, MapTileLayer, MapZoomControl } from '../components/ui/map';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAsync } from '../hooks/useAsync';
import { formatDate, formatDateTime, formatPrice } from '../utils/formatters';

export default function StationPage() {
  const { id } = useParams();
  const detail = useAsync(() => getStation(id), [id]);
  const options = useMemo(() => detail.data?.fuel_options || [], [detail.data?.fuel_options]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [benchmarkMetric, setBenchmarkMetric] = useState('average_price');

  useEffect(() => {
    if (!selectedKey && options.length) {
      const preferred = options.find(x => x.canonical_fuel_code === 'BENZINA' && Number(x.is_self) === 1) || options[0];
      setSelectedKey(`${preferred.fuel_catalog_id}-${preferred.is_self}`);
    }
  }, [options, selectedKey]);

  const selected = useMemo(() => options.find(x => `${x.fuel_catalog_id}-${x.is_self}` === selectedKey) || null, [options, selectedKey]);
  const to = detail.data?.latest_date || dayjs().format('YYYY-MM-DD');
  const from = dayjs(to).subtract(12, 'month').format('YYYY-MM-DD');
  const service = selected && Number(selected.is_self) === 0 ? 'served' : 'self';
  const history = useAsync(() => selected ? getStationHistory(id, { from, to, fuel_id: selected.fuel_catalog_id, fuel: selected.canonical_fuel_code, service }) : Promise.resolve({ items: [] }), [id, selected?.fuel_catalog_id, selected?.canonical_fuel_code, service, from, to]);
  const benchmarks = useAsync(() => selected && selected.canonical_fuel_code !== 'ALTRO' ? getStationBenchmarks(id, { from, to, fuel: selected.canonical_fuel_code, service }) : Promise.resolve(null), [id, selected?.canonical_fuel_code, service, from, to]);

  if (detail.loading) return <LoadingState minHeight={650} />;
  if (detail.error) return <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6"><ErrorState error={detail.error} /></div>;

  const s = detail.data.station;
  const rows = history.data?.items || [];
  const first = rows[0];
  const last = rows.at(-1);
  const delta = first && last ? Number(last.price) - Number(first.price) : null;
  const pct = delta !== null && Number(first.price) !== 0 ? delta / Number(first.price) * 100 : null;
  const lat = Number(s.latitude);
  const lng = Number(s.longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng) && Number(s.coordinates_valid) === 1;

  return (
    <div className="mx-auto min-w-0 max-w-5xl px-4 pb-12 pt-5 sm:px-6 sm:pt-9">
      <Button asChild variant="ghost" className="mb-5 -ml-3 text-md-on-surface-variant hover:bg-md-surface-container hover:text-md-on-surface">
        <Link to="/mappa"><ArrowLeft className="mr-2 h-4 w-4" /> Torna alla mappa</Link>
      </Button>

      <section className="relative overflow-hidden rounded-[40px_40px_16px_40px] bg-md-primary-container p-6 sm:p-8">
        <div aria-hidden="true" className="absolute -right-8 -top-10 h-36 w-36 rounded-[64px_64px_16px_64px] bg-md-secondary" />
        <div className="relative z-10 max-w-3xl">
          <div className="flex flex-wrap gap-2">
            <Badge className="border-0 bg-md-surface text-md-on-surface">Impianto #{id}</Badge>
            <Badge className={s.current_status === 'active' ? 'border-0 bg-md-success-container text-md-on-success-container' : 'border-0 bg-md-surface-container-high text-md-on-surface-variant'}>{s.current_status === 'active' ? 'Attivo' : 'Non attivo'}</Badge>
          </div>
          <h1 className="mt-5 break-words text-[2.15rem] font-medium leading-[1.04] tracking-[-0.04em] text-md-on-primary-container min-[390px]:text-[2.35rem] sm:text-[3.25rem]">{s.nome_impianto || `Impianto ${id}`}</h1>
          <div className="mt-3 flex max-w-2xl items-start gap-2 text-sm leading-6 text-md-on-primary-container"><MapPin className="mt-1 h-4 w-4 shrink-0" />{[s.indirizzo, s.comune, s.provincia].filter(Boolean).join(' · ')}</div>
          {s.bandiera && <div className="mt-2 text-sm font-bold text-md-on-primary-container">{s.bandiera}</div>}
        </div>
      </section>

      <section className="mt-7">
        <SectionLabel>Scegli il rifornimento</SectionLabel>
        <div className="no-scrollbar -mx-1 mt-3 flex gap-2 overflow-x-auto px-1 pb-2">
          {options.map(o => {
            const key = `${o.fuel_catalog_id}-${o.is_self}`;
            const active = key === selectedKey;
            return (
              <Button
                key={key}
                variant="ghost"
                onClick={() => setSelectedKey(key)}
                className={`h-auto min-h-[60px] shrink-0 px-5 py-3 ${active ? 'bg-md-primary text-md-on-primary hover:bg-md-primary' : 'bg-md-surface-container text-md-on-surface hover:bg-md-primary-container hover:text-md-on-primary-container'}`}
              >
                <span className="text-left">
                  <span className="block text-sm font-bold">{o.raw_fuel_description}</span>
                  <span className={`mt-0.5 block text-[11px] font-medium ${active ? 'text-md-on-primary' : 'text-md-on-surface-variant'}`}>{Number(o.is_self) === 1 ? 'Self-service' : 'Servito'}</span>
                </span>
              </Button>
            );
          })}
        </div>
      </section>

      <section className="mt-5 overflow-hidden rounded-[40px_16px_40px_40px] bg-md-secondary-container p-3 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-[1.2fr_.8fr]">
          <div className="rounded-[30px_30px_10px_30px] bg-md-surface p-5 sm:p-6">
            <div className="text-xs font-medium text-md-on-surface-variant">Prezzo attuale</div>
            <div className="numbers mt-1 break-words text-[2.75rem] font-bold leading-none tracking-[-0.055em] text-md-on-surface min-[390px]:text-[3.2rem] sm:text-[3.6rem]">{last ? formatPrice(last.price, selected?.canonical_fuel_code) : '—'}</div>
            <div className="mt-4"><PriceChangePill value={pct} /></div>
          </div>
          <div className="grid min-w-0 gap-2 min-[480px]:grid-cols-3 sm:grid-cols-1">
            <Mini label="12 mesi fa" value={first ? formatPrice(first.price, selected?.canonical_fuel_code) : '—'} />
            <Mini label="Differenza" value={delta === null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(3)} €`} />
            <Mini label="Giornate" value={rows.length} />
          </div>
        </div>
        <div className="mt-3 rounded-[30px_30px_10px_30px] bg-md-surface p-1 sm:p-4">
          {history.loading ? <LoadingState minHeight={350} /> : history.error ? <ErrorState error={history.error} /> : <PriceTrendChart series={rows} actual showRange={false} height={370} fuel={selected?.canonical_fuel_code} />}
        </div>
      </section>

      {selected?.canonical_fuel_code !== 'ALTRO' && (
        <section className="mt-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <SectionLabel tone="secondary">Confronto territoriale</SectionLabel>
              <h2 className="mt-4 max-w-2xl text-[2rem] font-medium leading-tight tracking-[-0.03em] text-md-on-surface sm:text-[2.6rem]">Questo impianto rispetto a comune, provincia, regione e Italia.</h2>
            </div>
            <Tabs value={benchmarkMetric} onValueChange={setBenchmarkMetric}>
              <TabsList className="bg-md-surface-container">
                <TabsTrigger value="average_price" className="data-[state=active]:bg-md-secondary data-[state=active]:text-md-on-secondary">Media</TabsTrigger>
                <TabsTrigger value="median_price" className="data-[state=active]:bg-md-secondary data-[state=active]:text-md-on-secondary">Mediana</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="mt-5 overflow-hidden rounded-[16px_40px_40px_40px] bg-md-surface-container p-2 sm:p-5">
            <div className="rounded-[30px] bg-md-surface p-1 sm:p-3">
              {benchmarks.loading ? <LoadingState minHeight={420} /> : benchmarks.error ? <ErrorState error={benchmarks.error} /> : <BenchmarkChart data={benchmarks.data} metric={benchmarkMetric} fuel={selected?.canonical_fuel_code} />}
            </div>
          </div>
        </section>
      )}

      <section className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="rounded-[36px_36px_14px_36px] bg-md-surface-container p-6">
          <div className="flex items-center gap-3"><div className="grid h-11 w-11 place-items-center rounded-[16px] bg-md-primary-container text-md-on-primary-container"><Store className="h-5 w-5" /></div><h3 className="text-xl font-medium text-md-on-surface">Informazioni impianto</h3></div>
          <div className="mt-4"><Info icon={Store} label="Gestore" value={s.gestore} /><Info icon={Route} label="Tipologia" value={s.tipo_impianto} /><Info icon={Fuel} label="Prima rilevazione" value={formatDate(s.first_seen_date)} /></div>
        </div>
        <div className="rounded-[36px_14px_36px_36px] bg-md-tertiary-container p-6 text-md-on-tertiary-container">
          <div className="flex items-center gap-3"><div className="grid h-11 w-11 place-items-center rounded-[16px] bg-md-surface text-md-on-surface"><Fuel className="h-5 w-5" /></div><h3 className="text-xl font-medium">Ultimi prezzi comunicati</h3></div>
          <div className="mt-3 divide-y divide-[#cbb566]">{detail.data.prices.slice(0, 6).map((p, i) => <div key={`${p.fuel_catalog_id}-${p.is_self}-${i}`} className="flex items-center gap-3 py-3.5"><div className="min-w-0 flex-1"><div className="truncate text-sm font-bold">{p.raw_fuel_description}</div><div className="text-xs">{Number(p.is_self) === 1 ? 'Self' : 'Servito'} · {formatDateTime(p.communicated_at)}</div></div><div className="numbers text-sm font-bold">{formatPrice(p.price, p.canonical_fuel_code)}</div><ChevronRight className="h-4 w-4" /></div>)}</div>
        </div>
      </section>

      {hasCoords && (
        <section className="mt-10 overflow-hidden rounded-[40px_40px_14px_40px] bg-md-surface-container">
          <div className="p-6 sm:p-7"><SectionLabel tone="neutral" className="bg-md-surface">Posizione</SectionLabel><h2 className="mt-3 text-2xl font-medium tracking-[-0.025em] text-md-on-surface">Dove si trova</h2><p className="mt-1 text-sm text-md-on-surface-variant">Coordinate dichiarate nell’anagrafica MIMIT.</p></div>
          <div className="h-[360px]"><Map center={[lat, lng]} zoom={15} className="h-full min-h-0 rounded-none border-0"><MapTileLayer /><MapZoomControl /><MapMarker position={[lat, lng]}><MapPopup><b>{s.nome_impianto}</b><br />{s.indirizzo}</MapPopup></MapMarker></Map></div>
        </section>
      )}
    </div>
  );
}

function Mini({ label, value }) {
  return <div className="min-w-0 rounded-[22px] bg-md-surface-container-low px-4 py-4"><div className="text-[11px] font-medium text-md-on-surface-variant">{label}</div><div className="numbers mt-1 break-words text-lg font-bold text-md-on-surface">{value}</div></div>;
}
function Info({ icon: Icon, label, value }) {
  return <div className="flex items-start gap-3 border-b border-md-outline-variant py-3.5 last:border-0"><Icon className="mt-0.5 h-4 w-4 text-md-secondary" /><div className="min-w-0"><div className="text-xs text-md-on-surface-variant">{label}</div><div className="mt-0.5 text-sm font-medium text-md-on-surface">{value || '—'}</div></div></div>;
}
